import { SPACE_ACCENT } from './ui';
import type { SiteId } from '@goodbuild/rules/sites';

/**
 * The tab, in the colours of the side you are standing on.
 *
 * Section 04 gives each audience a ground and one accent so that a person knows
 * where they are before they read a word, and `tokens.css` paints the page that
 * way already. A tab is read before a word too — often instead of one, by
 * somebody with eight of them open — and all three sides drew the same orange
 * mark. A studio with the portal and the shop open side by side had two
 * identical favicons to tell them apart by, and the pinned one was a coin toss.
 *
 * **This has to happen at runtime, and the storefront's equivalent does not.**
 * Astro renders a page per side and can put the right `<link>` in the head as
 * it goes. This app is one HTML file, served for every route, whose audience
 * comes from the path — `/portal` is a studio's, `/board` is a creator's — and
 * the path changes without the document ever being fetched again. So the
 * elements in `index.html` carry the store's set, which is what a first paint
 * can honestly claim, and this replaces them once the router knows better.
 *
 * The files are generated by `pnpm brand` into `apps/app/public/`, five per
 * side, named by the same suffix rule the storefront uses.
 */

/** How a side's files are named. The store's are bare; see `tools/brand-mark.mjs`. */
function suffixFor(space: SiteId): string {
  return space === 'store' ? '' : `-${space}`;
}

/**
 * Every element this owns, marked in `index.html` so it can find them again.
 *
 * An attribute rather than a `rel` sweep: `rel="icon"` appears twice, a browser
 * extension is free to add a third, and replacing something we did not put
 * there is how a tab ends up showing somebody else's picture.
 */
const OWNED = '[data-space-icon]';

/**
 * Swap the head's icons to a side's.
 *
 * **Replaced rather than retitled.** Setting `href` on a live `<link rel="icon">`
 * is the documented way and is not reliably honoured: Chrome and Safari both
 * have long-standing form where an in-place `href` change is ignored until
 * something else invalidates the head, which shows up as the icon changing on a
 * hard refresh and never on a navigation — the exact case this exists for.
 * Removing the node and appending a new one is what every browser treats as a
 * new declaration.
 *
 * Idempotent, and safe to call on a route that has not changed side: the new
 * set is built first and the old one removed only once it is ready, so there is
 * no frame with no icon declared at all.
 */
export function applySpaceIcons(space: SiteId): void {
  if (typeof document === 'undefined') return;

  const head = document.head;
  const at = suffixFor(space);

  // Nothing to do if the head already says this. Cheap, and it keeps a router
  // that re-renders on every state change from rebuilding five elements each
  // time.
  const current = head.querySelector<HTMLLinkElement>('link[rel="manifest"][data-space-icon]');
  if (current?.getAttribute('href') === `/site${at}.webmanifest`) return;

  const link = (rel: string, href: string, extra?: Record<string, string>) => {
    const element = document.createElement('link');
    element.rel = rel;
    element.setAttribute('href', href);
    for (const [name, value] of Object.entries(extra ?? {})) element.setAttribute(name, value);
    element.setAttribute('data-space-icon', '');
    return element;
  };

  const themeColour = document.createElement('meta');
  themeColour.name = 'theme-color';
  themeColour.content = SPACE_ACCENT[space];
  themeColour.setAttribute('data-space-icon', '');

  const replacements: Element[] = [
    link('icon', `/favicon${at}.ico`, { sizes: '32x32' }),
    link('icon', `/favicon${at}.svg`, { type: 'image/svg+xml' }),
    link('apple-touch-icon', `/apple-touch-icon${at}.png`),
    link('manifest', `/site${at}.webmanifest`),
    themeColour,
  ];

  for (const stale of head.querySelectorAll(OWNED)) stale.remove();
  for (const element of replacements) head.appendChild(element);
}
